import React, { useState, useRef, useCallback, useEffect } from 'react'
import { ArrowLeft, Loader2, FolderOpen } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Session, RoomSection, HistoryItem, Detection, SectionValidationResult, RoomSectionCreate, SectionExpectedItemCreate, ItemValidationResult } from '@/types'
import { SectionWithImages } from '@/utils/sectionStatus'
import { apiService } from '@/services/api'
import MobileSectionCarousel from './MobileSectionCarousel'
import MobileDetectionViewer from './MobileDetectionViewer'
import MobileDetectionDetails from './MobileDetectionDetails'
import MobileSessionOverview from './MobileSessionOverview'
import MobileValidationResults from './MobileValidationResults'
import MobileSectionCreationDialog from './MobileSectionCreationDialog'

type ViewMode = 'carousel' | 'viewer' | 'overview'

interface MobileSessionViewProps {
  session: Session
  sections: RoomSection[]
  sectionsWithStatus: SectionWithImages[]
  analyses: HistoryItem[]
  isSessionCompleted: boolean
  isSessionReady: boolean
  isAdmin?: boolean
  loadingSections?: boolean
  settings: {
    conf_threshold?: number
    show_uncertain_detections?: boolean
    [key: string]: any
  }
  classValidationStatus: Map<string, 'match' | 'mismatch' | 'unexpected'>
  onUploadComplete: (result: any, sectionId: number | null) => void
  onClassCorrection: (detectionId: number, newClass: string) => void
  onSessionComplete: () => void
  onViewSectionDetails: (section: RoomSection) => void
  onNavigateBack: () => void
  onStartNewSession?: () => void
  loadAnalysisImage: (analysisId: number) => Promise<string | null>
  onCreateSection?: (sectionData: RoomSectionCreate) => Promise<RoomSection>
  onAssignImageToSection?: (imageId: number, sectionId: number) => Promise<void>
}

const MobileSessionView: React.FC<MobileSessionViewProps> = ({
  session,
  sections,
  sectionsWithStatus,
  analyses,
  isSessionCompleted,
  isSessionReady,
  isAdmin = false,
  loadingSections = false,
  settings,
  classValidationStatus,
  onUploadComplete,
  onClassCorrection,
  onSessionComplete,
  onViewSectionDetails,
  onNavigateBack,
  onStartNewSession,
  loadAnalysisImage,
  onCreateSection,
  onAssignImageToSection,
}) => {
  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('carousel')
  const [viewingAnalysis, setViewingAnalysis] = useState<HistoryItem | null>(null)

  // Persist section index in sessionStorage (survives reload, not new tabs)
  const storageKey = `session-${session.id}-section-index`
  const [currentSectionIndex, setCurrentSectionIndex] = useState(() => {
    const saved = sessionStorage.getItem(storageKey)
    return saved ? parseInt(saved, 10) : 0
  })

  // Save section index when it changes
  useEffect(() => {
    sessionStorage.setItem(storageKey, currentSectionIndex.toString())
  }, [currentSectionIndex, storageKey])

  // Ensure index is valid when sections change
  // Allow index === sections.length for "Create New Section" card (if session not completed)
  const canCreateSection = !isSessionCompleted && onCreateSection
  const maxValidIndex = canCreateSection ? sections.length : sections.length - 1
  useEffect(() => {
    if (sections.length > 0 && currentSectionIndex > maxValidIndex) {
      // If saved index is beyond valid range, reset to last valid index
      setCurrentSectionIndex(Math.max(0, maxValidIndex))
    }
  }, [sections.length, currentSectionIndex, maxValidIndex])
  const [viewingImageUrl, setViewingImageUrl] = useState<string>('')
  const [selectedDetection, setSelectedDetection] = useState<Detection | null>(null)

  // Upload state
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  // Validation dialog state
  const [showValidationDialog, setShowValidationDialog] = useState(false)
  const [validationResult, setValidationResult] = useState<SectionValidationResult | null>(null)

  // Section creation dialog state
  const [showSectionCreationDialog, setShowSectionCreationDialog] = useState(false)
  const [pendingNewSectionAnalysis, setPendingNewSectionAnalysis] = useState<HistoryItem | null>(null)
  const [pendingNewSectionImageUrl, setPendingNewSectionImageUrl] = useState<string | null>(null)
  const [isCreatingSection, setIsCreatingSection] = useState(false)

  // Cache for loaded image URLs (keyed by analysisId)
  const [imageUrlCache, setImageUrlCache] = useState<Record<number, string>>({})

  // Track which analysis IDs we've already attempted to load (prevents duplicate requests)
  const loadedAnalysisIds = useRef<Set<number>>(new Set())

  // Cache for validation results by section ID
  const [validationCache, setValidationCache] = useState<Record<number, SectionValidationResult>>({})
  const loadedValidationSectionIds = useRef<Set<number>>(new Set())

  // Track recently uploaded images per section (keyed by sectionId)
  // This ensures thumbnails update immediately after upload, before parent state refreshes
  const [recentUploadBySectionId, setRecentUploadBySectionId] = useState<Record<number, string>>({})

  // Preload image URLs for all sections with analyses on mount
  // This ensures carousel thumbnails show uploaded images immediately
  useEffect(() => {
    const preloadSectionImages = async () => {
      const analysisIdsToLoad: number[] = []

      // Collect all analysis IDs that need loading
      sectionsWithStatus.forEach((sectionData) => {
        if (sectionData.images && sectionData.images.length > 0) {
          const latestImage = sectionData.images[0]
          // Only load if not already attempted
          if (!loadedAnalysisIds.current.has(latestImage.id)) {
            analysisIdsToLoad.push(latestImage.id)
            loadedAnalysisIds.current.add(latestImage.id)
          }
        }
      })

      if (analysisIdsToLoad.length === 0) return

      // Load images in parallel
      const results = await Promise.all(
        analysisIdsToLoad.map(async (analysisId) => {
          try {
            const url = await loadAnalysisImage(analysisId)
            return { analysisId, url }
          } catch (error) {
            console.error(`Failed to preload image for analysis ${analysisId}:`, error)
            return { analysisId, url: null }
          }
        })
      )

      // Update cache with all loaded URLs
      setImageUrlCache((prev) => {
        const newCache = { ...prev }
        results.forEach(({ analysisId, url }) => {
          if (url) {
            newCache[analysisId] = url
          }
        })
        return newCache
      })
    }

    preloadSectionImages()
  }, [sectionsWithStatus, loadAnalysisImage]) // Re-run when sections change

  // Preload validation data for all sections with images
  useEffect(() => {
    const preloadValidationData = async () => {
      const sectionsToLoad: { sectionId: number; analysisId: number }[] = []

      sectionsWithStatus.forEach((sectionData) => {
        if (sectionData.images && sectionData.images.length > 0) {
          const sectionId = sectionData.section.id
          const latestImage = sectionData.images[0]
          if (!loadedValidationSectionIds.current.has(sectionId)) {
            sectionsToLoad.push({ sectionId, analysisId: latestImage.id })
            loadedValidationSectionIds.current.add(sectionId)
          }
        }
      })

      if (sectionsToLoad.length === 0) return

      const results = await Promise.all(
        sectionsToLoad.map(async ({ sectionId, analysisId }) => {
          try {
            const validation = await apiService.validateSectionAnalysis(sectionId, analysisId)
            return { sectionId, validation }
          } catch (error) {
            console.error(`Failed to preload validation for section ${sectionId}:`, error)
            return { sectionId, validation: null }
          }
        })
      )

      setValidationCache((prev) => {
        const newCache = { ...prev }
        results.forEach(({ sectionId, validation }) => {
          if (validation) {
            newCache[sectionId] = validation
          }
        })
        return newCache
      })
    }

    preloadValidationData()
  }, [sectionsWithStatus])

  // Helper to compute validation counts from validation result
  // Counts actual items (not classes) - sums the detected/expected quantities
  const getValidationCounts = useCallback((sectionId: number): { match: number; mismatch: number; missing: number; unexpected: number } | null => {
    const validation = validationCache[sectionId]
    if (!validation) return null

    const counts = { match: 0, mismatch: 0, missing: 0, unexpected: 0 }
    validation.validation_items.forEach((item) => {
      if (item.status === 'match') {
        counts.match += item.detected
      } else if (item.status === 'mismatch') {
        counts.mismatch += item.detected
      } else if (item.status === 'missing') {
        // Missing items weren't detected - count the expected quantity that's missing
        counts.missing += item.expected
      } else if (item.status === 'unexpected') {
        counts.unexpected += item.detected
      }
    })
    return counts
  }, [validationCache])

  // File input refs for existing sections
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const pendingSectionId = useRef<number | null>(null)

  // File input refs for new section creation
  const newSectionCameraInputRef = useRef<HTMLInputElement>(null)
  const newSectionGalleryInputRef = useRef<HTMLInputElement>(null)

  // Get current section
  const currentSection = sections[currentSectionIndex]
  const currentSectionData = sectionsWithStatus.find(
    (s) => s.section.id === currentSection?.id
  )

  // Handle camera button click
  const handleTakePhoto = useCallback((sectionId: number) => {
    pendingSectionId.current = sectionId
    cameraInputRef.current?.click()
  }, [])

  // Handle gallery button click
  const handleChooseGallery = useCallback((sectionId: number) => {
    pendingSectionId.current = sectionId
    galleryInputRef.current?.click()
  }, [])

  // Handle file selection
  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      const sectionId = pendingSectionId.current

      console.log('[MobileUpload] handleFileSelect called', { file: file?.name, sectionId, sessionId: session.id })

      if (!file || !sectionId) {
        console.warn('[MobileUpload] Early return - missing file or sectionId', { hasFile: !!file, sectionId })
        return
      }

      // Reset input
      event.target.value = ''

      // Create a preview URL from the file IMMEDIATELY for instant thumbnail update
      const localPreviewUrl = URL.createObjectURL(file)
      setRecentUploadBySectionId((prev) => ({ ...prev, [sectionId]: localPreviewUrl }))

      try {
        setIsUploading(true)
        setUploadProgress(0)

        // Simulate progress during upload
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => Math.min(prev + 10, 90))
        }, 200)

        console.log('[MobileUpload] Calling analyzeImage API', { session_id: session.id, section_id: sectionId })

        // Call API to analyze image
        const result = await apiService.analyzeImage(file, {
          session_id: session.id,
          section_id: sectionId,
          confidence_threshold: settings.conf_threshold,
          show_uncertain_detections: settings.show_uncertain_detections,
        })

        console.log('[MobileUpload] API response received', { analysis_id: result.analysis_id, total_detections: result.total_detections })

        clearInterval(progressInterval)
        setUploadProgress(100)

        // Notify parent of upload completion
        onUploadComplete(result, sectionId)

        // Load the image blob for viewing (higher quality)
        const analysisId = result.analysis_id
        if (analysisId) {
          const imageUrl = await loadAnalysisImage(analysisId)
          if (imageUrl) {
            // Cache the loaded blob URL by analysisId
            setImageUrlCache((prev) => ({ ...prev, [analysisId]: imageUrl }))
            // Update section thumbnail with blob URL (replaces local preview)
            setRecentUploadBySectionId((prev) => ({ ...prev, [sectionId]: imageUrl }))
            // Revoke the local preview URL to free memory
            URL.revokeObjectURL(localPreviewUrl)
            setViewingAnalysis({
              ...result,
              id: result.analysis_id,
              section_id: sectionId,
            } as HistoryItem)
            setViewingImageUrl(imageUrl)
            setViewMode('viewer')
          }
        }
      } catch (error) {
        console.error('Upload failed:', error)
        alert('Failed to upload image. Please try again.')
        // Revert the preview on error - remove from cache and free memory
        setRecentUploadBySectionId((prev) => {
          const updated = { ...prev }
          delete updated[sectionId]
          return updated
        })
        URL.revokeObjectURL(localPreviewUrl)
      } finally {
        setIsUploading(false)
        setUploadProgress(0)
        pendingSectionId.current = null
      }
    },
    [session.id, settings, onUploadComplete, loadAnalysisImage]
  )

  // Handle next section after viewing
  const handleNextSection = useCallback(() => {
    const isLastSection = currentSectionIndex >= sections.length - 1

    if (isLastSection) {
      setViewMode('overview')
    } else {
      setCurrentSectionIndex((prev) => prev + 1)
      setViewMode('carousel')
      setViewingAnalysis(null)
      setViewingImageUrl('')
    }
  }, [currentSectionIndex, sections.length])

  // Handle detection click for correction
  const handleDetectionClick = useCallback((detection: Detection) => {
    setSelectedDetection(detection)
  }, [])

  // Handle class correction
  const handleCorrection = useCallback(
    (newClass: string) => {
      if (selectedDetection) {
        onClassCorrection(selectedDetection.id, newClass)
        setSelectedDetection(null)
      }
    },
    [selectedDetection, onClassCorrection]
  )

  // Handle viewing validation breakdown
  const handleViewValidation = useCallback(async () => {
    if (!viewingAnalysis || !viewingAnalysis.section_id) return

    try {
      const validation = await apiService.validateSectionAnalysis(
        viewingAnalysis.section_id,
        viewingAnalysis.id
      )
      setValidationResult(validation)
      setShowValidationDialog(true)
    } catch (error) {
      console.error('Failed to fetch validation:', error)
    }
  }, [viewingAnalysis])

  // Handle viewing detections from carousel
  const handleViewDetectionsFromCarousel = useCallback(
    async (sectionIndex: number) => {
      setCurrentSectionIndex(sectionIndex)
      const sectionData = sectionsWithStatus[sectionIndex]

      if (sectionData?.images.length > 0) {
        const latestImage = sectionData.images[0]
        try {
          // Check cache first
          let imageUrl = imageUrlCache[latestImage.id]
          if (!imageUrl) {
            imageUrl = await loadAnalysisImage(latestImage.id) || ''
            if (imageUrl) {
              setImageUrlCache((prev) => ({ ...prev, [latestImage.id]: imageUrl }))
            }
          }

          if (imageUrl) {
            setViewingAnalysis(latestImage)
            setViewingImageUrl(imageUrl)
            setViewMode('viewer')
          }
        } catch (error) {
          console.error('Failed to load image:', error)
        }
      }
    },
    [sectionsWithStatus, imageUrlCache, loadAnalysisImage]
  )

  // Handle viewing validation from carousel
  const handleViewValidationFromCarousel = useCallback(
    async (sectionId: number) => {
      const sectionData = sectionsWithStatus.find((s) => s.section.id === sectionId)

      if (sectionData && sectionData.images && sectionData.images.length > 0) {
        const latestImage = sectionData.images[0]
        try {
          const validation = await apiService.validateSectionAnalysis(
            sectionId,
            latestImage.id
          )
          setValidationResult(validation)
          setShowValidationDialog(true)
        } catch (error) {
          console.error('Failed to fetch validation:', error)
        }
      }
    },
    [sectionsWithStatus]
  )

  // Get cached image URL (synchronous, for carousel display)
  const getCachedImageUrl = useCallback(
    (analysisId: number): string | null => {
      return imageUrlCache[analysisId] || null
    },
    [imageUrlCache]
  )

  // Handle section tap from overview
  const handleOverviewSectionTap = useCallback(
    async (sectionIndex: number) => {
      setCurrentSectionIndex(sectionIndex)

      // Check if section has images
      const sectionData = sectionsWithStatus[sectionIndex]
      if (sectionData?.images.length > 0) {
        // Load the latest image for viewing
        const latestImage = sectionData.images[0]
        try {
          const imageUrl = await loadAnalysisImage(latestImage.id)
          if (imageUrl) {
            setViewingAnalysis(latestImage)
            setViewingImageUrl(imageUrl)
            setViewMode('viewer')
          } else {
            setViewMode('carousel')
          }
        } catch (error) {
          console.error('Failed to load image:', error)
          setViewMode('carousel')
        }
      } else {
        setViewMode('carousel')
      }
    },
    [sectionsWithStatus, loadAnalysisImage]
  )

  // Handle back to carousel from overview
  const handleBackFromOverview = useCallback(() => {
    setViewMode('carousel')
  }, [])

  // Handle session completion
  const handleComplete = useCallback(() => {
    if (window.confirm('Complete this session? You won\'t be able to add more images.')) {
      onSessionComplete()
    }
  }, [onSessionComplete])

  // Handle create section - take photo
  const handleCreateSectionTakePhoto = useCallback(() => {
    newSectionCameraInputRef.current?.click()
  }, [])

  // Handle create section - choose from gallery
  const handleCreateSectionChooseGallery = useCallback(() => {
    newSectionGalleryInputRef.current?.click()
  }, [])

  // Handle file selection for new section creation
  const handleNewSectionFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]

      console.log('[MobileUpload] handleNewSectionFileSelect called', { file: file?.name, sessionId: session.id })

      if (!file) {
        console.warn('[MobileUpload] Early return - no file selected')
        return
      }

      // Reset input
      event.target.value = ''

      try {
        setIsUploading(true)
        setUploadProgress(0)

        // Simulate progress during upload
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => Math.min(prev + 10, 90))
        }, 200)

        console.log('[MobileUpload] Calling analyzeImage API (new section)', { session_id: session.id })

        // Upload without section_id to create unassigned image
        const result = await apiService.analyzeImage(file, {
          session_id: session.id,
          section_id: undefined, // No section - will be assigned after creation
          confidence_threshold: settings.conf_threshold,
          show_uncertain_detections: settings.show_uncertain_detections,
        })

        console.log('[MobileUpload] API response received (new section)', { analysis_id: result.analysis_id })

        clearInterval(progressInterval)
        setUploadProgress(100)

        // Notify parent of upload completion (with null section_id)
        onUploadComplete(result, null)

        // Load the image for the dialog
        const analysisId = result.analysis_id
        if (analysisId) {
          const imageUrl = await loadAnalysisImage(analysisId)
          if (imageUrl) {
            // Cache the loaded URL
            setImageUrlCache((prev) => ({ ...prev, [analysisId]: imageUrl }))

            // Create analysis object for the dialog
            const newAnalysis: HistoryItem = {
              ...result,
              id: result.analysis_id,
              section_id: null,
            } as HistoryItem

            // Show section creation dialog
            setPendingNewSectionAnalysis(newAnalysis)
            setPendingNewSectionImageUrl(imageUrl)
            setShowSectionCreationDialog(true)
          }
        }
      } catch (error) {
        console.error('Upload failed:', error)
        alert('Failed to upload image. Please try again.')
      } finally {
        setIsUploading(false)
        setUploadProgress(0)
      }
    },
    [session.id, settings, onUploadComplete, loadAnalysisImage]
  )

  // Handle section creation confirmation
  const handleCreateSectionConfirm = useCallback(
    async (sectionName: string) => {
      if (!pendingNewSectionAnalysis || !onCreateSection || !onAssignImageToSection) return

      setIsCreatingSection(true)
      try {
        // Extract detected classes from the analysis to use as expected items
        const detectedClasses: SectionExpectedItemCreate[] = []
        if (pendingNewSectionAnalysis.detections) {
          const classMap = new Map<string, number>()
          pendingNewSectionAnalysis.detections.forEach((detection) => {
            const className = detection.user_corrected_class ||
              (detection.top_classes && detection.top_classes.length > 0
                ? (detection.top_classes[0].class_name || detection.top_classes[0].label)
                : null)
            if (className) {
              classMap.set(className, (classMap.get(className) || 0) + 1)
            }
          })
          classMap.forEach((count, className) => {
            detectedClasses.push({ class_name: className, expected_quantity: count })
          })
        }

        // Create the section
        if (!session.room_id) {
          throw new Error('Session has no room_id')
        }
        const sectionData: RoomSectionCreate = {
          room_id: session.room_id,
          name: sectionName.trim(),
          image_path: pendingNewSectionImageUrl || undefined,
          expected_items: detectedClasses,
        }

        const newSection = await onCreateSection(sectionData)

        // Assign the image to the newly created section
        await onAssignImageToSection(pendingNewSectionAnalysis.id, newSection.id)

        // Navigate to the newly created section (it will be at the end)
        setCurrentSectionIndex(sections.length) // Will be the new section's index

        // Close dialog and clean up
        setShowSectionCreationDialog(false)
        setPendingNewSectionAnalysis(null)
        setPendingNewSectionImageUrl(null)
      } catch (error) {
        console.error('Failed to create section:', error)
        alert('Failed to create section. Please try again.')
      } finally {
        setIsCreatingSection(false)
      }
    },
    [pendingNewSectionAnalysis, pendingNewSectionImageUrl, onCreateSection, onAssignImageToSection, session.room_id, sections.length]
  )

  // Handle section creation cancel
  const handleCreateSectionCancel = useCallback(() => {
    setShowSectionCreationDialog(false)
    setPendingNewSectionAnalysis(null)
    setPendingNewSectionImageUrl(null)
  }, [])

  // Render upload progress overlay
  const renderUploadingOverlay = () => {
    if (!isUploading) return null

    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-6 mx-4 max-w-sm w-full text-center">
          <Loader2 className="h-12 w-12 text-primary-600 animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Analyzing Image
          </h3>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-sm text-gray-500">
            Detecting items...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Hidden file inputs for existing sections */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Hidden file inputs for new section creation */}
      <input
        ref={newSectionCameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleNewSectionFileSelect}
        className="hidden"
      />
      <input
        ref={newSectionGalleryInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        onChange={handleNewSectionFileSelect}
        className="hidden"
      />

      {/* Header - only show in carousel mode */}
      {viewMode === 'carousel' && (
        <div className="bg-white border-b px-4 py-3 flex items-center gap-3 safe-area-top">
          <button
            onClick={onNavigateBack}
            className="p-2 -ml-2 text-gray-600 active:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-gray-900 truncate">
              {session.name || 'Inventory Session'}
            </h1>
            <p className="text-sm text-gray-500">
              {session.room_name || 'Room'}
            </p>
          </div>
          {!isSessionCompleted && sections.length > 0 && (
            <button
              onClick={() => setViewMode('overview')}
              className="text-sm text-primary-600 font-medium px-3 py-1.5 rounded-lg active:bg-primary-50"
            >
              Summary
            </button>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'carousel' && sections.length === 0 && loadingSections && (
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-gray-500">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary-500" />
              <p>Loading sections...</p>
            </div>
          </div>
        )}

        {viewMode === 'carousel' && sections.length === 0 && !loadingSections && (
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-gray-500 px-6">
              <FolderOpen className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p className="text-lg font-medium text-gray-700 mb-1">No sections yet</p>
              <p className="text-sm">This room doesn't have any sections defined.</p>
            </div>
          </div>
        )}

        {viewMode === 'carousel' && sections.length > 0 && (
          <div className="h-full flex flex-col pt-4">
            <MobileSectionCarousel
              sections={sections}
              sectionsWithStatus={sectionsWithStatus}
              currentIndex={currentSectionIndex}
              isSessionCompleted={isSessionCompleted}
              onIndexChange={setCurrentSectionIndex}
              onTakePhoto={handleTakePhoto}
              onChooseGallery={handleChooseGallery}
              onViewDetails={onViewSectionDetails}
              onReupload={handleChooseGallery}
              onViewDetections={handleViewDetectionsFromCarousel}
              onViewValidation={handleViewValidationFromCarousel}
              loadAnalysisImageUrl={getCachedImageUrl}
              recentUploadBySectionId={recentUploadBySectionId}
              onCreateSectionTakePhoto={onCreateSection ? handleCreateSectionTakePhoto : undefined}
              onCreateSectionChooseGallery={onCreateSection ? handleCreateSectionChooseGallery : undefined}
              getValidationCounts={getValidationCounts}
            />
          </div>
        )}

        {viewMode === 'viewer' && viewingAnalysis && (
          <MobileDetectionViewer
            analysis={viewingAnalysis}
            imageUrl={viewingImageUrl}
            validationStatus={classValidationStatus}
            validationCounts={viewingAnalysis.section_id ? getValidationCounts(viewingAnalysis.section_id) : null}
            onDetectionClick={handleDetectionClick}
            onNextSection={handleNextSection}
            onViewValidation={handleViewValidation}
            onBack={() => {
              setViewMode('carousel')
              setViewingAnalysis(null)
              setViewingImageUrl('')
            }}
            hasNextSection={currentSectionIndex < sections.length - 1}
            isLastSection={currentSectionIndex >= sections.length - 1}
          />
        )}

        {viewMode === 'overview' && (
          <MobileSessionOverview
            session={session}
            sections={sections}
            sectionsWithStatus={sectionsWithStatus}
            isSessionReady={isSessionReady}
            onSectionTap={handleOverviewSectionTap}
            onComplete={handleComplete}
            onBack={handleBackFromOverview}
            onStartNewSession={onStartNewSession}
          />
        )}
      </div>

      {/* Detection Details Modal */}
      {selectedDetection && (
        <MobileDetectionDetails
          detection={selectedDetection}
          cropUrl={selectedDetection.crop_path}
          validationStatus={
            classValidationStatus.get(
              selectedDetection.user_corrected_class ||
                selectedDetection.top_classes?.[0]?.label ||
                ''
            )
          }
          isAdmin={isAdmin}
          onCorrect={handleCorrection}
          onClose={() => setSelectedDetection(null)}
        />
      )}

      {/* Upload Progress Overlay */}
      {renderUploadingOverlay()}

      {/* Mobile Validation Results */}
      <MobileValidationResults
        isOpen={showValidationDialog}
        onClose={() => {
          setShowValidationDialog(false)
          setValidationResult(null)
        }}
        validationResult={validationResult}
      />

      {/* Section Creation Dialog */}
      <MobileSectionCreationDialog
        isOpen={showSectionCreationDialog}
        onClose={handleCreateSectionCancel}
        onConfirm={handleCreateSectionConfirm}
        analysis={pendingNewSectionAnalysis}
        imageUrl={pendingNewSectionImageUrl}
        isCreating={isCreatingSection}
      />
    </div>
  )
}

export default MobileSessionView
